// The Analytics hub's chart host. Stands in for the clinician demo's src/components/charts/EChart.tsx (@661ec4c), which
// wraps `echarts-for-react`; PA draws ECharts through `echarts/core` (as device-trends-card.tsx does), so this keeps the
// demo's props (option, height, ariaLabel, theme) and its behaviour: SVG renderer, `notMerge`, a resize one frame after
// mount and whenever the box changes size (the demo's item 18 fix). The height is handed to ECharts at init, so the
// host needs no inline style.
import { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { BarChart, BoxplotChart, HeatmapChart, LineChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent, VisualMapComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import type { EChartsOption } from 'echarts';

echarts.use([BarChart, BoxplotChart, HeatmapChart, LineChart, GridComponent, LegendComponent, TooltipComponent, VisualMapComponent, SVGRenderer]);

type Props = {
  option: EChartsOption;
  height: number;
  ariaLabel: string;
  /** A registered ECharts theme name (./echarts-theme.ts registers 'scrips'). Omitted = no theme. */
  theme?: string;
};

export default function EChart({ option, height, ariaLabel, theme }: Props) {
  const wrap = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const instance = echarts.init(el, theme, { renderer: 'svg', height });
    chart.current = instance;
    let frame = 0;
    const resize = () => {
      window.cancelAnimationFrame?.(frame);
      frame = window.requestAnimationFrame?.(() => instance.resize({ height })) ?? 0;
    };
    resize();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      window.cancelAnimationFrame?.(frame);
      instance.dispose();
      chart.current = null;
    };
  }, [theme, height]);

  useEffect(() => {
    chart.current?.setOption(option, { notMerge: true, lazyUpdate: true });
  }, [option, theme, height]);

  return <div ref={wrap} role="img" aria-label={ariaLabel} data-echart />;
}
